import InputAdornment from "@mui/material/InputAdornment";
import CustomInput from "../../../../common/components/CustomInput";
import { ChangeEvent, useCallback, useMemo, useRef } from "react";
import {
	Action,
	Field,
	FieldGroup,
	Party,
	Pricing,
	updatePartiesLoader,
} from "../../pricingListSlice";
import PricingModal from "../PricingModal";
import { Formik, FormikErrors, FormikProps, FormikValues } from "formik";
import { useActionFieldValidation } from "../../../../common/hooks/useActionFieldValidation";
import CustomAutocomplete from "../../../../common/components/CustomAutocomplete";
import { getParties } from "../../pricingList.thunk";
import { useAppDispatch } from "../../../../common/hooks/default";
import { useSelector } from "react-redux";
import {
	selectParties,
	selectPartiesLoader,
	selectPricingOptionsDefaultValue,
	selectPricingOptionsLoader,
	selectTsoDefaultValue,
	selectTsoLoader,
	selectTsoWarning,
} from "../../pricingList.selector";
import { ValidationError } from "../../../../common/hooks/usePydanticErrorsFormat";
import {
	Alert,
	Box,
	FormLabel,
	IconButton,
	List,
	ListItem,
	ListItemText,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import * as _ from "lodash";
import { useDeepMemo } from "../../../../common/hooks/useDeepMemo";
import { selectTender } from "../../../tender_form/tenderForm.selector";
import { If } from "../../../../common/components/If";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import AddIcon from "@mui/icons-material/Add";
import RadioButtonField from "../../../../common/components/RadioButtonField/RadioButtonField";
import { CustomToggleButtonGroup } from "../../../../common/components/ToggleButtonGroup/ToggleButtonGroup";
import { enrichLabel } from "../../utils/labels.utils";
import CustomSelect from "../../../../common/components/CustomSelect";
import { getFieldsMetadata } from "../../utils/action.utils";
import { useTranslation } from "react-i18next";

interface ActionPricingModalProps {
	open: boolean;
	onClose: () => void;
	forPricings: Pricing[];
	onSubmit: (
		body: Record<
			string,
			| string
			| number
			| boolean
			| { asset_id: string; timeserie_id: number }[]
		>
	) => void;
	isLoading: boolean;
	action?: Action | null;
	errorMessage: string | ValidationError | undefined | null;
	hasMorePricings?: boolean;
	sites: Record<
		string,
		{ asset_id: string; timeserie_id: number; name: string }
	>;
	handleSelectedSite: (
		event: React.ChangeEvent<HTMLInputElement>,
		checked: boolean
	) => void;
}

export function mapParty(party: Party | undefined) {
	if (party)
		return {
			id: party.party_id,
			partyId: party.party_id,
			name: party.name,
			shortName: party.short_name,
		};
}

function formatFormikError(
	error:
		| string
		| string[]
		| null
		| undefined
		| FormikErrors<any>
		| FormikErrors<any>[]
): string | undefined {
	if (!error) {
		return undefined;
	}
	if (Array.isArray(error)) {
		if (!error.length) {
			return undefined;
		}
		if (typeof error[0] === "string") {
			return error.join(", ");
		}
		return undefined;
	}
	if (typeof error === "string") {
		return error;
	}
	return "";
}

export default function ActionPricingModal(props: ActionPricingModalProps) {
	const {
		open,
		isLoading,
		forPricings,
		action,
		errorMessage,
		onClose,
		onSubmit,
		handleSelectedSite,
		sites,
	} = props;
	const { t } = useTranslation();
	const formRef = useRef<FormikProps<FormikValues>>();
	const defaultTso = useSelector(selectTsoDefaultValue);
	const defaultPricingOptions = useSelector(selectPricingOptionsDefaultValue);
	const tsoLoader = useSelector(selectTsoLoader);
	const pricingOptionsLoader = useSelector(selectPricingOptionsLoader);
	const tsoWarning = useSelector(selectTsoWarning);

	const actionFields = useMemo(() => {
		if (!action?.fields) {
			return {};
		}
		
		const actionClone = _.cloneDeep(action);
		Object.keys(actionClone.fields).forEach((key) => {
			actionClone.fields[key].display_name = t(actionClone.fields[key].display_name ?? "");
			actionClone.fields[key].title = t(actionClone.fields[key].title ?? "");
		});
		return actionClone?.fields;
	}, [action?.fields, t]);

	const filterCascadeMapping = (value?: string | number, mapping?: any) => {
		if (!value) {
			return [];
		}
		if (!mapping) {
			return [];
		}

		return Object.keys(mapping).map((k) => {
			return { key: k, values: mapping[k][value] };
		});
	};

	const setSelectableItems = (
		fieldName: string,
		values: FormikValues,
		selectableValues?: { value: string; label: string }[]
	): {
		key: string | number;
		value: string | number;
		label?: string;
	}[] => {
		if (values[`${fieldName}_items`]) {
			return values[`${fieldName}_items`];
		}
		return (selectableValues || []).map((element) => {
			return {
				key: element.value,
				value: element.value,
				label: element.label,
			};
		});
	};

	// the reference to the action, but when compared deep it does not changes
	// so we actually don't want it to re-render
	const fields = useDeepMemo(() => {
		if (action) {
			const newFields: Record<string, any> = {};
			_.toPairs(actionFields).forEach(([fieldName, field]) => {
				if (field.is_array) {
					const arrayFieldName = fieldName + "_array";
					const arrayField = { ...field, label: arrayFieldName };
					newFields[arrayFieldName] = arrayField;
					newFields[fieldName] = {
						...field,
						is_array: false,
						required: false,
					};
				} else {
					newFields[fieldName] = field;
				}
			});
			return newFields;
		}
		return {};
	}, [action]);

	function prepareValuesFromActionFields(fieldsToPrepare: {
		[key: string]: Field | FieldGroup;
	}) {
		const values: Record<string, any> = {};
		_.toPairs(fieldsToPrepare).forEach(([fieldName, field]) => {
			if (field.is_array) {
				const arrayFieldName = fieldName + "_array";
				values[arrayFieldName] = [];
			} else if (field.name == "tso_id") {
				values[fieldName] = defaultTso;
			} else if (field.name == "pricingOptions") {
				values[fieldName] = defaultPricingOptions;
			} else {
				if (Array.isArray(field.label)) {
					values[fieldName + "_label"] = field.default_label;
				}

				// try to map fieldname with a field from summary in order to prefill
				let prefillValues: any = undefined;
				if (field.default_value !==null) {
					values[fieldName] = field.default_value;
				} else {
					if (field.prefill) {
						prefillValues = forPricings.map((pricing) => {
							if (Array.isArray(field.prefill!)) {
								return _.get(
									pricing,
									field.prefill.find((x) => {
										return !!_.get(pricing, x);
									})
								);
							} else {
								return _.get(pricing, field.prefill!);
							}
						});

						prefillValues = prefillValues.filter(
							(element: number) => element !== null
						);
						if (field.default_label == "%" && prefillValues[0]) {
							prefillValues[0] = prefillValues[0] * 100;
						}
					}
					values[fieldName] = prefillValues
						? prefillValues[0]
						: undefined;
				}
				if (field.cascade_mapping) {
					const val = values[fieldName];
					Object.keys(field.cascade_mapping).forEach((key) => {
						values[`${key}_items`] =
							field.cascade_mapping[key][val];
					});
				}
			}
		});
		return values;
	}

	// useDeepMemo instead of use memo because an incoming message would change
	// the reference to the action, but when compared deep it does not changes
	// so we actually don't want it to re-render
	const initialValues = useDeepMemo(() => {
		if (!action) {
			return {};
		}

		if (action?.fields?.party) {
			return {
				party: mapParty({
					id: forPricings[0].party?.party_id,
					name: forPricings[0].party?.name || "",
					party_id: forPricings[0].party?.party_id || 0,
					short_name: forPricings[0].party?.short_name || "",
				}),
				mirror_book: forPricings[0]?.mirror_book,
			};
		}

		/**
		 * For array fields we alter the values we pass to formik.
		 * For regular fields we should end with:
		 * {
		 * 	fieldA: undefined,
		 *  fieldB: undefined
		 * }
		 *
		 * But for arrays we want:
		 * {
		 *   fieldA_array: [],
		 * }
		 *
		 */
		return prepareValuesFromActionFields(actionFields);
	}, [action, fields, forPricings, defaultTso]);

	const firstPricing = useMemo(() => forPricings?.[0], [forPricings]);

	const conflictingFields = useDeepMemo(() => {
		if (action) {
			const conflicting: Record<string, boolean> = {};
			_.toPairs(actionFields).forEach(([fieldName, field]) => {
				if (!field.is_array) {
					conflicting[fieldName] = false;
					if (field.prefill) {
						let values = forPricings.map((pricing) => {
							if (Array.isArray(field.prefill!)) {
								return _.get(
									pricing,
									field.prefill.find((x) => {
										return !!_.get(pricing, x);
									})
								);
							} else {
								return _.get(pricing, field.prefill!);
							}
						});
						values = values.filter((element) => element !== null);

						conflicting[fieldName] = values.reduce(
							(acc, curr) => acc || curr !== values[0],
							false
						);
					}
				}
			});
			return conflicting;
		}
		return {};
	}, [action, forPricings]);

	const fieldsMetadata = useMemo(
		() => getFieldsMetadata(forPricings, actionFields, action),
		[action, forPricings]
	);

	let validationSchema = useActionFieldValidation(
		fields || {},
		1,
		fieldsMetadata
	);
	const dispatch = useAppDispatch();
	const partiesLoader = useSelector(selectPartiesLoader);
	const parties = useSelector(selectParties);

	const fetchParties = (search: string) => {
		dispatch(updatePartiesLoader(true));
		dispatch(getParties(search));
	};
	const tender = useSelector(selectTender);
	const loaderDataAutoComplete = useMemo<boolean>(() => {
		return partiesLoader as boolean;
	}, [partiesLoader]);

	const fetchDataAutoComplete = useCallback(
		(fieldName: string) => {
			if (fieldName == "party") return parties;
			return [];
		},
		[parties, forPricings, tender]
	);

	const triggerAutocompleteDataFetch = (
		fieldName: string,
		search: string = ""
	): void => {
		if (fieldName == "party") {
			fetchParties(search);
		}
	};
	const customSetFieldValue = (
		setFieldValue: (
			field: string,
			value: any,
			shouldValidate?: boolean
		) => void,
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		if (e.target.name == "baseloadPrice" && action?.action === "SET_BETA") {
			const baseLoadPrice = parseFloat(e.target.value);
			const alphaClient =
				forPricings[0]?.pricing_runs[0].summary?.AlphaClient?.value ||
				0;

			const beta = alphaClient * baseLoadPrice - baseLoadPrice;
			setFieldValue("beta", beta);
		}
	};

	const getLoaderForRadioButton = useCallback(
		(fieldName: string, defaultLoader: boolean) => {
			if (action?.action && fieldName === "tso_id") {
				return tsoLoader || defaultLoader;
			}
			if (action?.action && fieldName === "pricingOptions") {
				return pricingOptionsLoader || defaultLoader;
			}
			return defaultLoader;
		},
		[action?.action, forPricings, tsoLoader, pricingOptionsLoader]
	);

	const getDefaultValue = useCallback(
		(value: string, fieldName: string) => {
			if (action?.action && fieldName === "tso_id" && defaultTso) {
				return value || defaultTso;
			}
			if (
				action?.action &&
				fieldName === "pricingOptions" &&
				defaultPricingOptions
			) {
				return value || defaultPricingOptions;
			}
			return value || "";
		},
		[action?.action, defaultTso, defaultPricingOptions]
	);

	const onModalClose = () => {
		onClose();
	};
	return (
		<PricingModal
			open={open}
			onClose={onModalClose}
			onSubmit={() => {
				if (formRef.current) {
					// @ts-ignore
					formRef.current.submitForm();
				}
			}}
			forPricings={forPricings}
			title={t(action?.display_name ?? "")}
			subTitle={
				props.hasMorePricings
					? `Applies to all possible pricings on the tender pricings, including these as example:`
					: "Applicable for the following pricings:"
			}
			warningMessage={tsoWarning}
			isSubmitDisabled={isLoading}
			isLoading={isLoading}
			errorMessage={errorMessage}
			action={action?.action}
			sites={sites}
			handleSelectedSite={handleSelectedSite}
		>
			<Formik
				key={"formik-action-pricing-modal"}
				//@ts-ignore
				innerRef={formRef}
				initialValues={initialValues}
				validationSchema={validationSchema}
				validateOnChange={false}
				validateOnBlur={false}
				enableReinitialize={true}
				onSubmit={async (values) => {
					onSubmit(values);
				}}
			>
				{({
					handleChange,
					handleSubmit,
					values,
					errors,
					setFieldValue,
					touched,
				}) => {
					return (
						<form
							noValidate
							key={"form-action-pricing-modal"}
							onSubmit={handleSubmit}
						>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: "5px",
								}}
							>
								{Object.entries(actionFields).map(
									(value, index) =>
										!fieldsMetadata[value[0]].hidden && (
											<Box
												key={`${value[0]}_container`}
												sx={{
													display: "flex",
													flexDirection: "column",
												}}
											>
												<Box
													sx={{
														display: "flex",
														flexDirection: "row",
														gap: "8px",
														alignItems: "center",
													}}
												>
													<If
														condition={
															value[1]
																?.display_name
																?.length
														}
													>
														<FormLabel
															sx={{
																width: "250px",
																wordBreak:
																	"break-all",
															}}
														>
															{enrichLabel(
																value[1]
																	?.display_name,
																firstPricing
															)}
														</FormLabel>
													</If>
													<Box
														sx={{
															display: "flex",
															alignItems:
																"center",
															flex: 1,
														}}
													>
														{value[1].component ==
															"radiogroup" && (
															<RadioButtonField
																loader={getLoaderForRadioButton(
																	value[0],
																	isLoading
																)}
																field={
																	value[1] as Field
																}
																onChange={(
																	event: React.ChangeEvent<HTMLInputElement>
																) => {
																	setFieldValue(
																		value[1]
																			.label as string,
																		event
																			.target
																			.value
																	);
																}}
																errors={formatFormikError(
																	errors[
																		(value[1]
																			.label as string) ||
																			""
																	]
																)}
																pricingId={
																	forPricings[0]
																		?.id
																}
																selectedValue={getDefaultValue(
																	values[
																		value[1]
																			.label as string
																	],
																	value[0]
																)}
															/>
														)}
														{value[1].component ==
															"select" && (
															<CustomSelect
																value={String(
																	values[
																		value[0]
																	]
																)}
																//@ts-ignore
																items={setSelectableItems(
																	value[0],
																	values,
																	value[1]
																		.selectable_values
																)}
																touched={true}
																name={
																	value[1]
																		.name as string
																}
																label={
																	value[1]
																		.label as string
																}
																onChange={async (
																	e
																) => {
																	await handleChange(
																		e
																	);
																	setFieldValue(
																		value[0],
																		e.target
																			.value
																	);
																	const items =
																		filterCascadeMapping(
																			e
																				.target
																				?.value as string,
																			value[1]
																				.cascade_mapping
																		);
																	items.forEach(
																		(
																			item
																		) => {
																			setFieldValue(
																				`${item.key}_items`,
																				item.values
																			);
																			setFieldValue(
																				`${item.key}`,
																				item
																					.values[0]
																					.value
																			);
																		}
																	);
																}}
																error={
																	!!errors[
																		value[1]
																			.name as string
																	]
																}
																errorText={
																	formatFormikError(
																		errors[
																			(value[1]
																				.name as string) ||
																				""
																		]
																	) ?? ""
																}
															/>
														)}
														{value[1].component ===
															"input" && (
															<>
																<CustomInput
																	data-testid={
																		"action-field-" +
																		value[0]
																	}
																	key={
																		value[1]
																			.title +
																		"input"
																	}
																	InputProps={{
																		endAdornment:
																			(
																				<InputAdornment position="start">
																					{!Array.isArray(
																						value[1]
																							.label
																					) &&
																						enrichLabel(
																							value[1]
																								.label,
																							firstPricing
																						)}
																				</InputAdornment>
																			),
																		type: value[1]
																			.type,
																	}}
																	rows={3}
																	name={
																		value[0]
																	}
																	autoFocus={
																		index ===
																		0
																	}
																	placeholder={
																		values[
																			value[0]
																		] ||
																		value[1]
																			.title
																	}
																	disabled={
																		fieldsMetadata[
																			value[0]
																		]
																			.disabled
																	}
																	fullWidth
																	onChange={async (
																		e
																	) => {
																		await handleChange(
																			e
																		);
																		customSetFieldValue(
																			setFieldValue,
																			e
																		);
																	}}
																	variant="standard"
																	// @ts-ignore
																	value={
																		values[
																			value[0]
																		]
																	}
																	// @ts-ignore
																	errorText={
																		errors[
																			value[0]
																		]
																	}
																	touched={
																		true
																	}
																/>
																{Array.isArray(
																	value[1]
																		.label
																) && (
																	<CustomToggleButtonGroup
																		sx={{
																			height: "40px",
																		}}
																		groupValue={
																			values[
																				value[0] +
																					"_label"
																			]
																		}
																		onValueChange={(
																			choice
																		) => {
																			setFieldValue(
																				value[0] +
																					"_label",
																				choice
																			);
																		}}
																		groupLabel="all-or-active-only"
																		buttonValues={
																			value[1]
																				.label
																		}
																		displayValues={value[1].label.map(
																			(
																				label
																			) =>
																				enrichLabel(
																					label,
																					firstPricing
																				)
																		)}
																	/>
																)}
															</>
														)}

														{value[1].component ==
															"autocomplete" && (
															<CustomAutocomplete
																key={
																	value[0] +
																	"autocomplete"
																}
																sx={{
																	"& .MuiInputLabel-shrink":
																		{
																			display:
																				"none",
																		},
																	width: "100%",
																	marginBottom: 0,
																}}
																fetchData={(
																	search
																) =>
																	triggerAutocompleteDataFetch(
																		value[0],
																		search
																	)
																}
																data={fetchDataAutoComplete(
																	value[0]
																)}
																label={enrichLabel(
																	value[1]
																		.label as string,
																	firstPricing
																)}
																name={value[0]}
																loading={
																	loaderDataAutoComplete
																}
																setFieldValue={
																	setFieldValue
																}
																renderOption={
																	value[0] ===
																	"proxy_refs"
																		? (
																				optionProps,
																				option,
																				state
																		  ) => {
																				if (
																					option ===
																					state.inputValue
																				) {
																					return (
																						<li
																							{...optionProps}
																							key={
																								option
																							}
																						>
																							<AddIcon />
																							<span
																								style={{
																									marginLeft: 8,
																								}}
																							>
																								{
																									option
																								}
																							</span>
																						</li>
																					);
																				}
																				return (
																					<li
																						{...optionProps}
																						key={
																							option.id
																						}
																					>
																						<If
																							condition={
																								option.mapped
																							}
																						>
																							<LinkIcon />
																						</If>
																						<If
																							condition={
																								!option.mapped
																							}
																						>
																							<LinkOffIcon />
																						</If>
																						<span
																							style={{
																								marginLeft: 8,
																							}}
																						>
																							{
																								option.name
																							}
																						</span>
																					</li>
																				);
																		  }
																		: undefined
																}
																value={
																	values[
																		value[0]
																	]
																}
																freeSolo={
																	value[0] ==
																	"proxy_refs"
																}
																error={
																	!!errors[
																		value[0]
																	] &&
																	!!touched[
																		value[0]
																	]
																}
																helperText={
																	!!errors[
																		value[0]
																	] &&
																	touched[
																		value[0]
																	]
																		? (
																				errors[
																					value[0]
																				] as any
																		  ).name
																		: ""
																}
															/>
														)}
														{value[1].is_array && (
															<>
																<IconButton
																	disabled={
																		!values[
																			value[0]
																		]
																	}
																	type="button"
																	color="primary"
																	onClick={() => {
																		const fieldValues =
																			values[
																				`${value[0]}_array`
																			];
																		fieldValues.push(
																			values[
																				value[0]
																			]
																		);
																		setFieldValue(
																			`${value[0]}_array`,
																			fieldValues
																		);
																		setFieldValue(
																			`${value[0]}`,
																			""
																		);
																	}}
																>
																	<AddCircleIcon />
																</IconButton>
															</>
														)}
													</Box>
													{value[1].is_array && (
														<>
															{errors[
																`${value[0]}_array`
															] &&
																touched[
																	`${value[0]}_array`
																] && (
																	<p
																		style={{
																			color: "red",
																		}}
																	>
																		-{" "}
																		{
																			errors[
																				`${value[0]}_array`
																			] as string
																		}
																	</p>
																)}
															<List
																dense={false}
																sx={{
																	width: "100%",
																}}
															>
																{!!values[
																	`${value[0]}_array`
																] &&
																	values[
																		`${value[0]}_array`
																	].map(
																		(
																			item:
																				| string
																				| {
																						id: number;
																						name: string;
																				  }
																		) => (
																			<ListItem
																				key={`${value[0]}_array`}
																				secondaryAction={
																					<IconButton
																						sx={{
																							marginRight:
																								"-15px",
																						}}
																						edge="end"
																						aria-label="delete"
																						onClick={() => {
																							const fieldValues =
																								values[
																									`${value[0]}_array`
																								];
																							const idx =
																								fieldValues.findIndex(
																									(
																										v: string
																									) =>
																										v ==
																										item
																								);
																							const newValues =
																								[
																									...fieldValues,
																								];
																							newValues.splice(
																								idx,
																								1
																							);
																							setFieldValue(
																								`${value[0]}_array`,
																								newValues
																							);
																							setFieldValue(
																								`${value[0]}`,
																								""
																							);
																						}}
																					>
																						<DoNotDisturbOnIcon color="primary" />
																					</IconButton>
																				}
																			>
																				<ListItemText
																					primary={
																						typeof item ==
																						"string"
																							? item
																							: item.name
																					}
																				/>
																			</ListItem>
																		)
																	)}
															</List>
														</>
													)}
												</Box>
												<If
													condition={
														conflictingFields[
															value[0]
														]
													}
												>
													<Alert severity="warning">
														There were multiple
														values for this field
													</Alert>
												</If>
											</Box>
										)
								)}
							</Box>
						</form>
					);
				}}
			</Formik>
		</PricingModal>
	);
}
